import React, { useMemo, useState } from 'react';
import useItemTypes from '../../../Hooks/useItemTypes';
import AddBoxMenu from '../../../Components/AddBoxMenu/AddBoxMenu.component';
import OptionsMenu from '../../../Components/OptionsMenu';
import { useAuth } from '../../../Contexts/AuthProvider';
import { useBoxes } from '../../../Contexts/BoxContext';
import { userHasPermission } from '../../../helpers';
import UnitJobDateRangeGlobalFilters from '../../../Components/filters/UnitJobDateRangeGlobalFilters';
import DownloadFileButton from '../../../Components/DownloadFileButton';
import useTranslation from 'Hooks/useTranslation';
import { Button } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import MapComponentModal from 'Components/MapComponentModal';
import QuickAddTiles from 'Components/QuickAddTiles/QuickAddTiles.component';
import useAddBoxSelect from 'Hooks/useAddBoxSelect';
import FolderOpenOutlinedIcon from '@material-ui/icons/FolderOpenOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';

const UnitGroupHeader = props => {

    const {
        onAddBox,
        title,
        onLoadTemplate,
        onSaveTemplate,
        onExportCsv,
        onGenerateAutoLubeReport,
        onChangeUnit,
        onRefreshNew,
        showMapBtn,
        gpsData
    } = props;

    const { currentUser } = useAuth();
    const {
        unitBoxes,
        currentUnit,
        currentUnitModel,
        isMainGroup,
        unitBoxesFilters,
        changeUnitGlobalFilters,
        refreshBoxes,
        dataExportUnitsRequest,
        setModalStep
    } = useBoxes();
    const { data: itemTypes } = useItemTypes('units');
    const translation = useTranslation();
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);

    const allowManageTemplate = useMemo(() => userHasPermission(currentUser, 'manage_templates'), [currentUser]);
    const allowAutoLubeReport = useMemo(() => currentUser?.showAutoLubeReport?.() ?? false, [currentUser]);

    const hideMapType = currentUnitModel?.latitude === undefined;
    const availableItemTypes = useMemo(
        () => hideMapType ? itemTypes.filter((item) => item.itemType !== "map") : itemTypes,
        [hideMapType, itemTypes]
    );
    const selectBox = useAddBoxSelect(onAddBox, currentUnitModel);

    // the same actions as in the Options menu, shown as tiles on the empty dashboard
    const getQuickActions = () => {
        if (!isMainGroup) return [];

        const actions = [
            {
                id: 'quickAddTile-loadTemplate',
                icon: FolderOpenOutlinedIcon,
                group: 'options',
                label: translation.dashboardLoadTemplateLabel,
                onClick: onLoadTemplate,
            },
        ];
        if (currentUser.canExportCsv()) {
            actions.push({
                id: 'quickAddTile-exportCsv',
                icon: GetAppOutlinedIcon,
                group: 'options',
                label: translation.exportUnitToCsvLabel,
                onClick: () => {
                    onExportCsv();
                    setModalStep('jobOptionsCsv');
                },
            });
        }
        if (allowAutoLubeReport) {
            actions.push({
                id: 'quickAddTile-generateAutoLubeReport',
                icon: AssessmentOutlinedIcon,
                group: 'options',
                label: 'Generate Auto Lube Report',
                onClick: onGenerateAutoLubeReport,
            });
        }

        return actions;
    };

    const renderOptionsButton = () => {
        if (!isMainGroup) return null;

        return (
            <OptionsMenu
                refreshDisabled={!unitBoxes.length}
                onRefresh={() => refreshBoxes('units')}
                onRefreshNew={onRefreshNew}
                onChangeUnit={onChangeUnit}
                changeUnitDisabled={!currentUnit}
                isCurrentJobSet={!!currentUnit}
                allowManageTemplate={allowManageTemplate}
                disableSaveTemplate={!currentUnit && unitBoxes.length === 0}
                onLoadTemplate={onLoadTemplate}
                onSaveTemplate={onSaveTemplate}
                onExportCsv={onExportCsv}
                exportCsvLabel={translation.exportUnitToCsvLabel}
                allowAutoLubeReport={allowAutoLubeReport}
                onGenerateAutoLubeReport={onGenerateAutoLubeReport}
            />
        );
    };

    const renderGlobalFilters = () => {
        if (!isMainGroup) return null;
        // if (!currentUnitModel) return null;

        return (
            <UnitJobDateRangeGlobalFilters
                tabType='units'
                unitUid={currentUnitModel?.uId}
                inline
                onChange={changeUnitGlobalFilters}
                initData={unitBoxesFilters}
            />
        );
    };

    return (
      <>
        <div className="pageBtnSection">
          <div className="part">
            <div className="btnGroup">
              <div className="item">
                <AddBoxMenu
                  itemTypes={availableItemTypes}
                  onAddBox={onAddBox}
                  currentUnitModel={currentUnitModel}
                />
              </div>
              <div className="item">{renderOptionsButton()}</div>
              <div className="item">
                <DownloadFileButton
                  getExportRequest={dataExportUnitsRequest}
                  tabType="units"
                />
              </div>
            </div>
          </div>
          <div className="part centered centeredMobile">
            <div className="flex">
              <Button
                onClick={() => {
                  onChangeUnit();
                }}
                className="asideNavItem"
                fullWidth
                endIcon={<ArrowDropDownIcon />}
                id="unitGroupHeaderUnitSelectBtn"
              >
                <span className="hasMaxWidth titleLimitSymbols">
                  {currentUnitModel ? currentUnitModel.name : "Select Unit"}
                </span>
              </Button>
              {showMapBtn && currentUnitModel?.uId ? (
                <Button
                  onClick={() => {
                    setIsMapModalOpen(true);
                  }}
                  id="unitGroupHeaderMapBtn"
                  style={{ width: "20px", height: "20px", padding: "0" }}
                >
                  <MyLocationIcon style={{ width: "20px", height: "20px" }} />
                </Button>
              ) : null}
            </div>
          </div>
          <div className="part end">{renderGlobalFilters()}</div>
        </div>
        {!unitBoxes.length && (
          <QuickAddTiles
            itemTypes={availableItemTypes}
            onSelect={selectBox}
            actions={getQuickActions()}
          />
        )}
        <MapComponentModal
          currentUnitModel={currentUnitModel}
          isOpen={isMapModalOpen}
          onCancel={() => setIsMapModalOpen(false)}
          section="units"
          gpsData={gpsData}
        />
      </>
    );
};

export default UnitGroupHeader;
